import React, { useMemo } from 'react';
import { Person, SeatPosition } from '../types';

interface StatsDisplayProps {
  persons: Person[];
  positions: SeatPosition[];
}

const StatsDisplay: React.FC<StatsDisplayProps> = ({ persons, positions }) => {
  const stats = useMemo(() => {
    // 총 인원 (레이블 제외, 예정 제외)
    const totalCount = persons.filter(p => p.department !== '레이블' && !p.isPlanned).length;
    
    // 총 pxd 인원 (예정 제외)
    const totalPxdCount = persons.filter(p => p.department === 'pxd' && !p.isPlanned).length;
    
    // 위치가 있는 사람들만 필터링 (레이블 제외, 예정 제외)
    const positionedPersons = persons.filter(person => 
      person.department !== '레이블' && !person.isPlanned && positions.some(pos => pos.personId === person.id)
    );
    
    // 사무실 인원 (x <= 1410px, 즉 grid x <= 75, 레이블 제외, 예정 제외)
    const officePersons = positionedPersons.filter(person => {
      const position = positions.find(pos => pos.personId === person.id);
      // 1410px / 18.75px(각 그리드 크기) = 75.2, 따라서 x <= 75
      return position && position.x <= 75;
    });
    
    const officeCount = officePersons.length;
    const officePxdCount = officePersons.filter(p => p.department === 'pxd').length;
    const officeExternalCount = officePersons.filter(p => 
      ['외주사', '계약직', '프리랜서'].includes(p.department)
    ).length;
    const officeInternCount = officePersons.filter(p => p.department === '인턴').length;
    
    // 파견 인원 (x > 1410px, 즉 grid x > 75, 레이블 제외, 예정 제외)
    const remotePersons = positionedPersons.filter(person => {
      const position = positions.find(pos => pos.personId === person.id);
      return position && position.x > 75;
    });
    
    const remoteCount = remotePersons.length;
    const remotePxdCount = remotePersons.filter(p => p.department === 'pxd').length;
    const remoteExternalCount = remotePersons.filter(p => 
      ['외주사', '계약직', '프리랜서'].includes(p.department)
    ).length;
    
    return {
      totalCount,
      totalPxdCount,
      officeCount,
      officePxdCount,
      officeExternalCount,
      officeInternCount,
      remoteCount,
      remotePxdCount,
      remoteExternalCount
    };
  }, [persons, positions]);

  return (
    <>
      <style>
        {`
          .stats-display {
            position: absolute;
            left: 330px;
            top: 40px;
            font-size: 13px;
            font-weight: 600;
            color: #000;
            z-index: 10;
            display: flex;
            gap: 12px;
          }
          
          .stats-group {
            display: flex;
            flex-direction: column;
            min-width: 76px;
          }
          
          .stats-divider {
            width: 1px;
            background-color: rgba(0, 0, 0, 0.1);
            align-self: stretch;
          }

          .stats-group-title {
            line-height: 1.2;
            display: flex;
            padding: 2px 6px;
            font-weight: 800;
            margin-bottom: 4px;
          }
          
          .stats-item {
            line-height: 1.2;
            display: flex;
            justify-content: space-between;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 500;
          }

          .stats-item:hover {
            background: #00000010;
          }
          
          .stats-label {
            text-align: left;
          }
          
          .stats-count {
            text-align: right;
          }
        `}
      </style>
      <div className="stats-display">
        {/* 그룹 1 */}
        <div className="stats-group">
            <div className="stats-group-title">모든 인원</div>
          <div className="stats-item">
            <span className="stats-label">전체</span>
            <span className="stats-count">{stats.totalCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">pxd</span>
            <span className="stats-count">{stats.totalPxdCount}</span>
          </div>
        </div>
        
        <div className="stats-divider"></div>
        
        {/* 그룹 2 */}
        <div className="stats-group">
        <div className="stats-group-title">사무실</div>
          <div className="stats-item">
            <span className="stats-label">전체</span>
            <span className="stats-count">{stats.officeCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">pxd</span>
            <span className="stats-count">{stats.officePxdCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">외주 등</span>
            <span className="stats-count">{stats.officeExternalCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">인턴</span>
            <span className="stats-count">{stats.officeInternCount}</span>
          </div>
        </div>
        
        <div className="stats-divider"></div>
        
        {/* 그룹 3 */}
        <div className="stats-group">
        <div className="stats-group-title">파견</div>
          <div className="stats-item">
            <span className="stats-label">전체</span>
            <span className="stats-count">{stats.remoteCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">pxd</span>
            <span className="stats-count">{stats.remotePxdCount}</span>
          </div>
          <div className="stats-item">
            <span className="stats-label">외주 등</span>
            <span className="stats-count">{stats.remoteExternalCount}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatsDisplay; 